import { APIS, URLS } from '@/shared/config/urls';
import type { PageResponse } from '@/shared/types/abstract-api-response';
import { request } from '@/shared/utils/request';
import type { CommunityFeed, LikeState } from '../types/community.types';
import type { KidsPost, KidsPostComment, NewKidsPost } from '../types/kidsPost.types';

const pageParams = (page: number, size: number) =>
  new URLSearchParams({ page: String(page), size: String(size) }).toString();

/** Timeline (every post) or Postingan Saya (the caller's), newest first. */
export const fetchKidsPosts = async (
  feed: CommunityFeed,
  page: number,
  size: number
): Promise<PageResponse<KidsPost>> => {
  const params = pageParams(page, size);
  const path = feed === 'mine' ? APIS.myKidsPosts(params) : APIS.kidsPosts(params);
  return await request.get(`${URLS.BACK_DSH}${path}`);
};

/** Multipart: the photos in display order and an optional caption. */
export const createKidsPost = async ({ caption, photos }: NewKidsPost): Promise<KidsPost> => {
  const form = new FormData();
  if (caption) form.append('caption', caption);
  for (const photo of photos) form.append('photos', photo);
  return await request.post(`${URLS.BACK_DSH}${APIS.createKidsPost}`, form);
};

/** Only the author's own post; 404 otherwise. */
export const deleteKidsPost = async (postId: string): Promise<void> => {
  await request.delete(`${URLS.BACK_DSH}${APIS.kidsPost(postId)}`);
};

/** Idempotent: liking twice keeps one like. */
export const setKidsPostLike = async (postId: string, liked: boolean): Promise<LikeState> => {
  const url = `${URLS.BACK_DSH}${APIS.kidsPostLike(postId)}`;
  return await (liked ? request.put(url) : request.delete(url));
};

/** Comments of a post, oldest first. */
export const fetchKidsPostComments = async (
  postId: string,
  page: number,
  size: number
): Promise<PageResponse<KidsPostComment>> => {
  return await request.get(
    `${URLS.BACK_DSH}${APIS.kidsPostComments(postId, pageParams(page, size))}`
  );
};

export const createKidsPostComment = async (
  postId: string,
  content: string
): Promise<KidsPostComment> => {
  return await request.post(`${URLS.BACK_DSH}${APIS.kidsPostComments(postId)}`, { content });
};

/** Only the author's own comment; 404 otherwise. */
export const deleteKidsPostComment = async (postId: string, commentId: string): Promise<void> => {
  await request.delete(`${URLS.BACK_DSH}${APIS.kidsPostComment(postId, commentId)}`);
};

export const setKidsPostCommentLike = async (
  postId: string,
  commentId: string,
  liked: boolean
): Promise<LikeState> => {
  const url = `${URLS.BACK_DSH}${APIS.kidsPostCommentLike(postId, commentId)}`;
  return await (liked ? request.put(url) : request.delete(url));
};
